import React, { useState } from 'react';
import { api, fmt } from '../utils/api';
import PlaidConnectButton from './PlaidConnectButton';

export default function AccountsSidebar({ accounts, onRefresh, householdId, defaultCollapsed = false }) {
  const [disconnecting, setDisconnecting] = useState(null);
  const [copied, setCopied] = useState(false);
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  const [disconnectTarget, setDisconnectTarget] = useState(null); // { institution, itemId } | null

  const copyHouseholdId = () => {
    navigator.clipboard.writeText(householdId);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!accounts) return null;

  const grouped = accounts.reduce((acc, acct) => {
    const key = acct.institution_name || 'Unknown';
    if (!acc[key]) acc[key] = { itemId: acct.item_id, accounts: [] };
    acc[key].accounts.push(acct);
    return acc;
  }, {});

  const confirmDisconnect = async (mode) => {
    if (!disconnectTarget) return;
    const { itemId } = disconnectTarget;
    setDisconnecting(itemId);
    setDisconnectTarget(null);
    try {
      await api.disconnectAccount(itemId, mode);
      onRefresh();
    } catch (err) {
      console.error('Disconnect error:', err);
      window.alert('Failed to disconnect. Please try again.');
    } finally {
      setDisconnecting(null);
    }
  };

  const totalBalance = accounts
    .filter((a) => a.type === 'depository')
    .reduce((sum, a) => sum + (parseFloat(a.current_balance) || 0), 0);

  const totalCredit = accounts
    .filter((a) => a.type === 'credit')
    .reduce((sum, a) => sum + (parseFloat(a.current_balance) || 0), 0);

  return (
    <>
    <div style={styles.sidebar}>
      <div style={styles.header}>
        <button style={styles.collapseToggle} onClick={() => setCollapsed((c) => !c)}>
          <h2 style={styles.title}>
            <span style={styles.chevron}>{collapsed ? '▸' : '▾'}</span> 🏦 Accounts
          </h2>
        </button>
        <PlaidConnectButton onSuccess={onRefresh} />
      </div>

      {collapsed ? (
        <div style={styles.collapsedSummary} onClick={() => setCollapsed(false)}>
          {accounts.length} account{accounts.length !== 1 ? 's' : ''} · tap to show
        </div>
      ) : (
        <>
      {householdId && (
        <div style={styles.inviteBox}>
          <div style={styles.inviteText}>
            To let someone else join this household (e.g. a spouse), have them
            select "Join Existing Household" when they sign up and enter this ID:
          </div>
          <div style={styles.inviteRow}>
            <code style={styles.inviteId}>{householdId}</code>
            <button style={styles.copyBtn} onClick={copyHouseholdId}>
              {copied ? '✓ Copied' : 'Copy'}
            </button>
          </div>
        </div>
      )}

      {accounts.length === 0 ? (
        <p style={styles.empty}>
          Connect your bank accounts and credit cards to get started.
        </p>
      ) : (
        <>
          <div className="summary-grid-2" style={styles.summaryRow}>
            <div style={styles.summaryBox}>
              <div style={styles.summaryLabel}>Cash</div>
              <div style={{ ...styles.summaryAmt, color: '#00d4aa' }}>
                {fmt.currency(totalBalance)}
              </div>
            </div>
            <div style={styles.summaryBox}>
              <div style={styles.summaryLabel}>Credit Owed</div>
              <div style={{ ...styles.summaryAmt, color: '#ef4444' }}>
                {fmt.currency(totalCredit)}
              </div>
            </div>
          </div>

          {Object.entries(grouped).map(([institution, { itemId, accounts: accts }]) => (
            <div key={institution} style={styles.institution}>
              <div style={styles.institutionHeader}>
                <div style={styles.institutionName}>{institution}</div>
                <button
                  style={styles.disconnectBtn}
                  onClick={() => setDisconnectTarget({ institution, itemId })}
                  disabled={disconnecting === itemId}
                >
                  {disconnecting === itemId ? '...' : 'Disconnect'}
                </button>
              </div>
              {accts.map((acct) => (
                <div key={acct.id} style={styles.acctRow}>
                  <div style={styles.acctLeft}>
                    <span style={styles.acctIcon}>
                      {acct.type === 'credit' ? '💳' : '🏦'}
                    </span>
                    <div>
                      <div style={styles.acctName}>{acct.name}</div>
                      {acct.mask && (
                        <div style={styles.acctMask}>••••{acct.mask}</div>
                      )}
                    </div>
                  </div>
                  <div style={styles.acctRight}>
                    <div style={{
                      ...styles.balance,
                      color: acct.type === 'credit' ? '#ef4444' : '#fff',
                    }}>
                      {fmt.currency(acct.current_balance)}
                    </div>
                    <div style={styles.acctType}>{acct.subtype}</div>
                  </div>
                </div>
              ))}
            </div>
          ))}
        </>
      )}
        </>
      )}
    </div>

    {disconnectTarget && (
      <div style={styles.modalOverlay} onClick={() => setDisconnectTarget(null)}>
        <div style={styles.modalCard} onClick={(e) => e.stopPropagation()}>
          <h3 style={styles.modalTitle}>Disconnect {disconnectTarget.institution}?</h3>
          <p style={styles.modalText}>
            Your bank account itself is unaffected either way - this only
            controls what happens here in Boring Budgeting.
          </p>
          <button style={styles.modalSoftBtn} onClick={() => confirmDisconnect('soft')}>
            Disconnect, but keep history
            <span style={styles.modalSubtext}>Stops syncing. Past transactions stay visible.</span>
          </button>
          <button style={styles.modalHardBtn} onClick={() => confirmDisconnect('hard')}>
            Disconnect and delete everything
            <span style={styles.modalSubtext}>Permanently removes all its transaction history.</span>
          </button>
          <button style={styles.modalCancelBtn} onClick={() => setDisconnectTarget(null)}>
            Cancel
          </button>
        </div>
      </div>
    )}
    </>
  );
}

const styles = {
  sidebar: {
    background: '#111827',
    border: '1px solid #1e2a3a',
    borderRadius: '16px',
    padding: '1.5rem',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1rem',
  },
  title: {
    margin: 0,
    fontSize: '1rem',
    fontWeight: '700',
    color: '#fff',
    fontFamily: "'Syne', sans-serif",
  },
  collapseToggle: {
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    padding: 0,
    textAlign: 'left',
  },
  chevron: {
    display: 'inline-block',
    color: '#6b7280',
    fontSize: '0.85rem',
    marginRight: '2px',
  },
  collapsedSummary: {
    color: '#6b7280',
    fontSize: '0.8rem',
    cursor: 'pointer',
    padding: '0.5rem 0',
    fontFamily: "'Syne', sans-serif",
  },
  connectBtn: {
    background: '#00d4aa',
    color: '#0a0f1e',
    border: 'none',
    borderRadius: '6px',
    padding: '6px 12px',
    fontSize: '0.75rem',
    fontWeight: '700',
    cursor: 'pointer',
    fontFamily: "'Syne', sans-serif",
  },
  empty: {
    color: '#4b5563',
    fontSize: '0.875rem',
    textAlign: 'center',
    padding: '1rem 0',
    margin: 0,
  },
  summaryRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '0.5rem',
    marginBottom: '1rem',
  },
  summaryBox: {
    background: '#0a0f1e',
    border: '1px solid #1e2a3a',
    borderRadius: '8px',
    padding: '0.75rem',
  },
  summaryLabel: {
    color: '#6b7280',
    fontSize: '0.65rem',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginBottom: '0.25rem',
    fontFamily: "'Syne', sans-serif",
  },
  summaryAmt: {
    fontSize: '1rem',
    fontWeight: '700',
    fontFamily: "'DM Mono', monospace",
  },
  institution: { marginBottom: '0.75rem' },
  inviteBox: {
    background: '#0a0f1e',
    border: '1px solid #1e2a3a',
    borderRadius: '10px',
    padding: '0.75rem',
    marginBottom: '1rem',
  },
  inviteText: {
    color: '#6b7280',
    fontSize: '0.7rem',
    lineHeight: '1.4',
    marginBottom: '0.5rem',
    fontFamily: "'Syne', sans-serif",
  },
  inviteRow: { display: 'flex', gap: '0.5rem', alignItems: 'center' },
  inviteId: {
    flex: 1,
    background: '#111827',
    border: '1px solid #1e2a3a',
    borderRadius: '6px',
    padding: '0.4rem 0.5rem',
    color: '#00d4aa',
    fontSize: '0.7rem',
    fontFamily: "'DM Mono', monospace",
    overflowX: 'auto',
    whiteSpace: 'nowrap',
  },
  copyBtn: {
    background: 'transparent',
    color: '#00d4aa',
    border: '1px solid #00d4aa',
    borderRadius: '6px',
    padding: '6px 10px',
    fontSize: '0.7rem',
    fontWeight: '600',
    cursor: 'pointer',
    fontFamily: "'Syne', sans-serif",
    flexShrink: 0,
  },
  institutionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '0.35rem',
  },
  institutionName: {
    color: '#6b7280',
    fontSize: '0.7rem',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    fontFamily: "'Syne', sans-serif",
  },
  modalOverlay: {
    position: 'fixed',
    top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(0,0,0,0.6)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '1rem',
  },
  modalCard: {
    background: '#111827',
    border: '1px solid #1e2a3a',
    borderRadius: '16px',
    padding: '1.5rem',
    maxWidth: '400px',
    width: '100%',
  },
  modalTitle: {
    margin: '0 0 0.5rem',
    color: '#fff',
    fontSize: '1.05rem',
    fontWeight: '700',
    fontFamily: "'Syne', sans-serif",
  },
  modalText: {
    color: '#9ca3af',
    fontSize: '0.8rem',
    lineHeight: '1.5',
    marginBottom: '1.25rem',
  },
  modalSoftBtn: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    width: '100%',
    background: 'rgba(0, 212, 170, 0.08)',
    border: '1px solid #00d4aa',
    color: '#00d4aa',
    borderRadius: '10px',
    padding: '0.75rem 1rem',
    fontSize: '0.85rem',
    fontWeight: '700',
    cursor: 'pointer',
    fontFamily: "'Syne', sans-serif",
    marginBottom: '0.6rem',
    textAlign: 'left',
  },
  modalHardBtn: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    width: '100%',
    background: 'rgba(239, 68, 68, 0.08)',
    border: '1px solid #ef4444',
    color: '#ef4444',
    borderRadius: '10px',
    padding: '0.75rem 1rem',
    fontSize: '0.85rem',
    fontWeight: '700',
    cursor: 'pointer',
    fontFamily: "'Syne', sans-serif",
    marginBottom: '0.6rem',
    textAlign: 'left',
  },
  modalSubtext: {
    color: 'inherit',
    opacity: 0.7,
    fontSize: '0.7rem',
    fontWeight: '400',
    marginTop: '2px',
  },
  modalCancelBtn: {
    width: '100%',
    background: 'transparent',
    border: '1px solid #1e2a3a',
    color: '#6b7280',
    borderRadius: '10px',
    padding: '0.6rem',
    fontSize: '0.8rem',
    cursor: 'pointer',
    fontFamily: "'Syne', sans-serif",
  },
  disconnectBtn: {
    background: 'transparent',
    color: '#ef4444',
    border: '1px solid #3f1f1f',
    borderRadius: '6px',
    padding: '3px 8px',
    fontSize: '0.65rem',
    fontWeight: '600',
    cursor: 'pointer',
    fontFamily: "'Syne', sans-serif",
  },
  acctRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0.5rem 0.6rem',
    background: '#0a0f1e',
    borderRadius: '8px',
    marginBottom: '0.25rem',
    border: '1px solid #1e2a3a',
  },
  acctLeft: { display: 'flex', alignItems: 'center', gap: '0.5rem' },
  acctIcon: { fontSize: '1rem' },
  acctName: {
    color: '#e5e7eb',
    fontSize: '0.8rem',
    fontWeight: '500',
    fontFamily: "'Syne', sans-serif",
  },
  acctMask: {
    color: '#4b5563',
    fontSize: '0.65rem',
    fontFamily: "'DM Mono', monospace",
  },
  acctRight: { textAlign: 'right' },
  balance: {
    fontSize: '0.875rem',
    fontWeight: '600',
    fontFamily: "'DM Mono', monospace",
  },
  acctType: {
    color: '#4b5563',
    fontSize: '0.65rem',
    textTransform: 'capitalize',
    fontFamily: "'DM Mono', monospace",
  },
};
